import { Input } from '@/components/ui/input'
import { DIVISOR_LABEL, METHOD_LABEL, WEEKDAYS } from '@/lib/hr'
import { cn } from '@/lib/utils'
import type { HRDivisor, HREmployeeInput, HRPayoutMethod, HRPayType } from '@/types/hr'
import { Field, NativeSelect } from './shared'

// The person's details. Pay, bank and CNIC fields only render for the admin;
// the backend refuses them from anyone else regardless.

export interface PersonFormValue {
  full_name: string
  phone: string
  designation: string
  department: string
  join_date: string
  weekly_off_days: number[]
  shift_start: string
  emergency_contact: string
  notes: string
  cnic: string
  pay_type: HRPayType
  absence_divisor: HRDivisor | ''
  payout_method: HRPayoutMethod
  bank_name: string
  account_title: string
  account_number: string
  rate: string
}

export const EMPTY_PERSON: PersonFormValue = {
  full_name: '',
  phone: '',
  designation: '',
  department: 'Other',
  join_date: '',
  weekly_off_days: [0],
  shift_start: '',
  emergency_contact: '',
  notes: '',
  cnic: '',
  pay_type: 'monthly',
  absence_divisor: '',
  payout_method: 'cash',
  bank_name: '',
  account_title: '',
  account_number: '',
  rate: '',
}

/** The changed fields only (for PATCH), or everything (for create). */
export function personPayload(v: PersonFormValue, opts: { admin: boolean; base?: PersonFormValue; withRate?: boolean }): HREmployeeInput {
  const out: HREmployeeInput = {}
  const changed = <K extends keyof PersonFormValue>(k: K) => !opts.base || JSON.stringify(opts.base[k]) !== JSON.stringify(v[k])
  const basic = ['full_name', 'phone', 'designation', 'department', 'join_date', 'shift_start', 'emergency_contact', 'notes'] as const
  for (const k of basic) if (changed(k)) (out as Record<string, unknown>)[k] = v[k]
  if (changed('weekly_off_days')) out.weekly_off_days = v.weekly_off_days
  if (opts.admin) {
    const pay = ['cnic', 'pay_type', 'absence_divisor', 'payout_method', 'bank_name', 'account_title', 'account_number'] as const
    for (const k of pay) if (changed(k)) (out as Record<string, unknown>)[k] = v[k]
    if (opts.withRate && v.rate !== '') out.rate = Number(v.rate)
  }
  return out
}

export function PersonForm({
  value,
  onChange,
  departments,
  admin,
  showRate,
}: {
  value: PersonFormValue
  onChange: (v: PersonFormValue) => void
  departments: string[]
  admin: boolean
  showRate?: boolean
}) {
  const set = <K extends keyof PersonFormValue>(k: K, val: PersonFormValue[K]) => onChange({ ...value, [k]: val })
  const toggleDay = (d: number) =>
    set('weekly_off_days', value.weekly_off_days.includes(d) ? value.weekly_off_days.filter((x) => x !== d) : [...value.weekly_off_days, d].sort())
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name">
          <Input value={value.full_name} onChange={(e) => set('full_name', e.target.value)} maxLength={120} />
        </Field>
        <Field label="Phone">
          <Input value={value.phone} onChange={(e) => set('phone', e.target.value)} maxLength={32} />
        </Field>
        <Field label="Job">
          <Input value={value.designation} onChange={(e) => set('designation', e.target.value)} placeholder="e.g. Cook, Waiter, Cleaner" maxLength={80} />
        </Field>
        <Field label="Department">
          <NativeSelect value={value.department} onChange={(e) => set('department', e.target.value)}>
            {departments.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <Field label="Joined on">
          <Input type="date" value={value.join_date} onChange={(e) => set('join_date', e.target.value)} />
        </Field>
        <Field label="Shift starts" hint="Optional — for spotting lates">
          <Input type="time" value={value.shift_start} onChange={(e) => set('shift_start', e.target.value)} />
        </Field>
      </div>
      <Field label="Weekly off">
        <div className="flex flex-wrap gap-1">
          {WEEKDAYS.map((d, i) => (
            <button
              key={d}
              type="button"
              onClick={() => toggleDay(i)}
              className={cn(
                'rounded-md border px-2.5 py-1 text-xs font-medium',
                value.weekly_off_days.includes(i) ? 'border-primary bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {d}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Emergency contact">
        <Input value={value.emergency_contact} onChange={(e) => set('emergency_contact', e.target.value)} maxLength={120} />
      </Field>

      {admin && (
        <div className="space-y-3 rounded-lg border p-3">
          <div className="text-sm font-medium">Pay (only the owner sees this)</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Paid">
              <NativeSelect value={value.pay_type} onChange={(e) => set('pay_type', e.target.value as HRPayType)}>
                <option value="monthly">Monthly salary</option>
                <option value="daily">Daily wage</option>
              </NativeSelect>
            </Field>
            {showRate && (
              <Field label={value.pay_type === 'daily' ? 'Wage per day' : 'Salary per month'}>
                <Input type="number" min={0} value={value.rate} onChange={(e) => set('rate', e.target.value)} />
              </Field>
            )}
            {value.pay_type === 'monthly' && (
              <Field label="One absent day costs">
                <NativeSelect value={value.absence_divisor} onChange={(e) => set('absence_divisor', e.target.value as HRDivisor | '')}>
                  <option value="">Restaurant default</option>
                  {(Object.keys(DIVISOR_LABEL) as HRDivisor[]).map((d) => (
                    <option key={d} value={d}>
                      Salary {DIVISOR_LABEL[d]}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
            )}
            <Field label="Usually paid by">
              <NativeSelect value={value.payout_method} onChange={(e) => set('payout_method', e.target.value as HRPayoutMethod)}>
                {(Object.keys(METHOD_LABEL) as HRPayoutMethod[]).map((m) => (
                  <option key={m} value={m}>
                    {METHOD_LABEL[m]}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            {value.payout_method !== 'cash' && (
              <>
                <Field label={value.payout_method === 'bank' ? 'Bank' : 'Wallet'}>
                  <Input value={value.bank_name} onChange={(e) => set('bank_name', e.target.value)} placeholder={value.payout_method === 'bank' ? 'e.g. HBL' : 'JazzCash'} />
                </Field>
                <Field label="Account title">
                  <Input value={value.account_title} onChange={(e) => set('account_title', e.target.value)} />
                </Field>
                <Field label={value.payout_method === 'bank' ? 'Account / IBAN' : 'Wallet number'}>
                  <Input value={value.account_number} onChange={(e) => set('account_number', e.target.value)} />
                </Field>
              </>
            )}
            <Field label="CNIC">
              <Input value={value.cnic} onChange={(e) => set('cnic', e.target.value)} placeholder="35202-1234567-1" maxLength={20} />
            </Field>
          </div>
        </div>
      )}
    </div>
  )
}
